import type {
  CajaMetrics,
  CajaMovimiento,
  CajaReporte,
  Clinic,
  ClinicsComparison,
  DataQuality,
  MetricsPeriodQuery,
  MetricsSummary,
  PacientesMetrics,
  PacientesRecurrencia,
  PacienteRecurrenciaDetalle,
  PacienteRecurrenciaTipo,
  PacientesNoVolvieronResumen,
  PacienteNoVolvio,
  MonthlyTrendPoint,
  PlanRankingRow,
  PlanBilling,
  BillingVsRevenue,
  ProfesionalesMetrics,
  ReportFormat,
  Sede,
  TurnosMetrics,
  PvpMetrics,
  OccupancyMetrics,
  LifetimeValueReport,
  AuditTrailRow,
  OwnerProfessionalsRanking,
} from '@/application/metrics';
import type { OwnerDashboard } from '@/application/metrics/ownerDashboardTypes';
import type { OwnerPvpRetentionDashboard } from '@/application/metrics/ownerPvpRetentionTypes';

/** Período (año/mes o rango anual) sin tenant, para endpoints multi-clínica del scope del usuario. */
export interface PeriodQuery {
  year: number;
  month?: number;
  fromMonth?: number;
  toMonth?: number;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3010/api';

type MetricsEndpoint = 'summary' | 'turnos' | 'pacientes' | 'caja' | 'profesionales' | 'recurrencia' | 'data-quality' | 'plan-billing' | 'billing-vs-revenue' | 'occupancy';

interface ErrorResponse {
  error?: string;
}

function appendPeriodParams(params: URLSearchParams, query: PeriodQuery): void {
  params.set('year', String(query.year));
  if (query.fromMonth !== undefined && query.toMonth !== undefined) {
    params.set('fromMonth', String(query.fromMonth));
    params.set('toMonth', String(query.toMonth));
  } else if (query.month !== undefined) {
    params.set('month', String(query.month));
  }
}

function buildMetricsUrl(endpoint: MetricsEndpoint, query: MetricsPeriodQuery): string {
  const params = new URLSearchParams();
  appendPeriodParams(params, query);

  if (query.consultorioId !== undefined) {
    params.set('consultorioId', String(query.consultorioId));
  }

  return `${API_BASE}/metrics/tenants/${encodeURIComponent(query.tenantId)}/${endpoint}?${params.toString()}`;
}

async function errorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as ErrorResponse;
    return body.error ?? 'metrics_request_failed';
  } catch {
    return 'metrics_request_failed';
  }
}

async function getMetric<T>(token: string, endpoint: MetricsEndpoint, query: MetricsPeriodQuery): Promise<T> {
  const response = await fetch(buildMetricsUrl(endpoint, query), {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  return (await response.json()) as T;
}

export function getMetricsSummary(token: string, query: MetricsPeriodQuery): Promise<MetricsSummary> {
  return getMetric<MetricsSummary>(token, 'summary', query);
}

export function getTurnosMetrics(token: string, query: MetricsPeriodQuery): Promise<TurnosMetrics> {
  return getMetric<TurnosMetrics>(token, 'turnos', query);
}

export function getPacientesMetrics(token: string, query: MetricsPeriodQuery): Promise<PacientesMetrics> {
  return getMetric<PacientesMetrics>(token, 'pacientes', query);
}

export function getCajaMetrics(token: string, query: MetricsPeriodQuery): Promise<CajaMetrics> {
  return getMetric<CajaMetrics>(token, 'caja', query);
}

export function getProfesionalesMetrics(token: string, query: MetricsPeriodQuery): Promise<ProfesionalesMetrics> {
  return getMetric<ProfesionalesMetrics>(token, 'profesionales', query);
}

/** Recurrencia de pacientes (nuevos vs recurrentes) de una clínica en el período. */
export function getPacientesRecurrencia(token: string, query: MetricsPeriodQuery): Promise<PacientesRecurrencia> {
  return getMetric<PacientesRecurrencia>(token, 'recurrencia', query);
}

/** Señales de calidad de datos (alertas §5) de una clínica en el período. */
export interface PacientesRecurrenciaDetalleQuery extends MetricsPeriodQuery {
  tipo?: 'todos' | PacienteRecurrenciaTipo;
}

export async function getPacientesRecurrenciaDetalle(
  token: string,
  query: PacientesRecurrenciaDetalleQuery
): Promise<PacienteRecurrenciaDetalle[]> {
  const params = new URLSearchParams({ year: String(query.year), month: String(query.month) });
  if (query.tipo && query.tipo !== 'todos') {
    params.set('tipo', query.tipo);
  }

  const response = await fetch(
    `${API_BASE}/metrics/tenants/${encodeURIComponent(query.tenantId)}/recurrencia/detalle?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  return (await response.json()) as PacienteRecurrenciaDetalle[];
}

/** Ventana de recencia (meses) para "pacientes que no volvieron". */
export interface NoVolvieronQuery extends MetricsPeriodQuery {
  desde?: number;
  hasta?: number;
}

function buildNoVolvieronUrl(query: NoVolvieronQuery, suffix: string): string {
  const params = new URLSearchParams({ year: String(query.year), month: String(query.month) });
  if (query.desde !== undefined) {
    params.set('desde', String(query.desde));
  }
  if (query.hasta !== undefined) {
    params.set('hasta', String(query.hasta));
  }
  return `${API_BASE}/metrics/tenants/${encodeURIComponent(query.tenantId)}/pacientes/no-volvieron${suffix}?${params.toString()}`;
}

/** Resumen (conteos) de pacientes que no volvieron, para el bloque del dashboard. */
export async function getPacientesNoVolvieronResumen(
  token: string,
  query: NoVolvieronQuery
): Promise<PacientesNoVolvieronResumen> {
  const response = await fetch(buildNoVolvieronUrl(query, '/resumen'), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }
  return (await response.json()) as PacientesNoVolvieronResumen;
}

/** Listado de pacientes que no volvieron (ventana de recencia), ordenado por última atención desc. */
export async function getPacientesNoVolvieron(token: string, query: NoVolvieronQuery): Promise<PacienteNoVolvio[]> {
  const response = await fetch(buildNoVolvieronUrl(query, ''), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }
  return (await response.json()) as PacienteNoVolvio[];
}

/** Evolución mensual (últimos `months` meses, default 12) de una clínica. */
export async function getMonthlyTrend(
  token: string,
  query: MetricsPeriodQuery & { months?: number }
): Promise<MonthlyTrendPoint[]> {
  const params = new URLSearchParams({ year: String(query.year), month: String(query.month) });
  if (query.months !== undefined) {
    params.set('months', String(query.months));
  }
  if (query.consultorioId !== undefined) {
    params.set('consultorioId', String(query.consultorioId));
  }
  const response = await fetch(
    `${API_BASE}/metrics/tenants/${encodeURIComponent(query.tenantId)}/tendencia?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }
  return (await response.json()) as MonthlyTrendPoint[];
}

/** Ranking de planes vendidos de una clínica (ventana de `months` meses, default backend 3). */
export async function getPlanRanking(
  token: string,
  query: MetricsPeriodQuery & { months?: number }
): Promise<PlanRankingRow[]> {
  const params = new URLSearchParams({ year: String(query.year), month: String(query.month) });
  if (query.months !== undefined) {
    params.set('months', String(query.months));
  }
  const response = await fetch(
    `${API_BASE}/metrics/tenants/${encodeURIComponent(query.tenantId)}/planes/ranking?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }
  return (await response.json()) as PlanRankingRow[];
}

export function getDataQuality(token: string, query: MetricsPeriodQuery): Promise<DataQuality> {
  return getMetric<DataQuality>(token, 'data-quality', query);
}

/** Estado de facturación del plan de una clínica en el período. */
export function getPlanBilling(token: string, query: MetricsPeriodQuery): Promise<PlanBilling> {
  return getMetric<PlanBilling>(token, 'plan-billing', query);
}

/** Cruce del plan (deuda) contra la recaudación de caja de una clínica en el período. */
export function getBillingVsRevenue(token: string, query: MetricsPeriodQuery): Promise<BillingVsRevenue> {
  return getMetric<BillingVsRevenue>(token, 'billing-vs-revenue', query);
}

export interface CajaMovimientosQuery {
  tenantId: string;
  year: number;
  month: number;
  consultorioId?: number;
  tipo?: 'ingreso' | 'egreso';
  /** Tope servidor (default 1000). */
  limit?: number;
}

/** Detalle de movimientos de caja de una clínica/período (ordenados por monto desc). */
export async function getCajaMovimientos(token: string, query: CajaMovimientosQuery): Promise<CajaMovimiento[]> {
  const params = new URLSearchParams({ year: String(query.year), month: String(query.month) });
  if (query.consultorioId !== undefined) {
    params.set('consultorioId', String(query.consultorioId));
  }
  if (query.tipo) {
    params.set('tipo', query.tipo);
  }
  if (query.limit !== undefined) {
    params.set('limit', String(query.limit));
  }
  const response = await fetch(
    `${API_BASE}/metrics/tenants/${encodeURIComponent(query.tenantId)}/caja/movimientos?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  return (await response.json()) as CajaMovimiento[];
}

/** Reporte de caja de una clínica/período: totales, comisiones y desglose por forma de pago. */
export async function getCajaReporte(token: string, query: MetricsPeriodQuery): Promise<CajaReporte> {
  const params = new URLSearchParams({ year: String(query.year), month: String(query.month) });
  if (query.consultorioId !== undefined) {
    params.set('consultorioId', String(query.consultorioId));
  }
  const response = await fetch(
    `${API_BASE}/metrics/tenants/${encodeURIComponent(query.tenantId)}/caja/reporte?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  return (await response.json()) as CajaReporte;
}

/** PVP clínico de una clínica/período (atendidos / primeras visitas). */
export async function getPvpMetrics(token: string, query: MetricsPeriodQuery): Promise<PvpMetrics> {
  const params = new URLSearchParams({ year: String(query.year), month: String(query.month) });
  if (query.consultorioId !== undefined) {
    params.set('consultorioId', String(query.consultorioId));
  }
  const response = await fetch(
    `${API_BASE}/metrics/tenants/${encodeURIComponent(query.tenantId)}/pvp?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  return (await response.json()) as PvpMetrics;
}

/** Ocupación de agenda por sede (capacidad teórica o proxy de turnos). */
export function getOccupancyMetrics(token: string, query: MetricsPeriodQuery): Promise<OccupancyMetrics> {
  return getMetric<OccupancyMetrics>(token, 'occupancy', query);
}

export interface LifetimeValueQuery extends MetricsPeriodQuery {
  allTime?: boolean;
  limit?: number;
}

/** LTV realizado (ingresos de caja por paciente). */
export async function getLifetimeValue(token: string, query: LifetimeValueQuery): Promise<LifetimeValueReport> {
  const params = new URLSearchParams();
  if (query.allTime) {
    params.set('allTime', '1');
  } else {
    params.set('year', String(query.year));
    params.set('month', String(query.month));
  }
  if (query.consultorioId !== undefined) {
    params.set('consultorioId', String(query.consultorioId));
  }
  if (query.limit !== undefined) {
    params.set('limit', String(query.limit));
  }
  const response = await fetch(
    `${API_BASE}/metrics/tenants/${encodeURIComponent(query.tenantId)}/lifetime-value?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }
  return (await response.json()) as LifetimeValueReport;
}

export interface AuditTrailQuery {
  from?: string;
  to?: string;
  accion?: string;
  limit?: number;
}

/** Trail de accesos/exports del panel visibles para el usuario. */
export async function getAuditTrail(token: string, query: AuditTrailQuery = {}): Promise<AuditTrailRow[]> {
  const params = new URLSearchParams();
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  if (query.accion) params.set('accion', query.accion);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  const qs = params.toString();
  const response = await fetch(`${API_BASE}/metrics/audit-trail${qs ? `?${qs}` : ''}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }
  return (await response.json()) as AuditTrailRow[];
}

/** Sedes (consultorios) de una clínica con su nombre real. */
export async function getSedes(token: string, tenantId: string): Promise<Sede[]> {
  const response = await fetch(`${API_BASE}/metrics/tenants/${encodeURIComponent(tenantId)}/sedes`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  return (await response.json()) as Sede[];
}

/** Clínicas del scope del usuario con su nombre (para no mostrar GUIDs). */
export async function getClinics(token: string): Promise<Clinic[]> {
  const response = await fetch(`${API_BASE}/metrics/clinics`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  return (await response.json()) as Clinic[];
}

/** Comparativa (JSON) de todas las clínicas del scope del usuario para un período. */
export async function getClinicsComparison(token: string, query: PeriodQuery): Promise<ClinicsComparison> {
  const params = new URLSearchParams({ year: String(query.year), month: String(query.month) });
  const response = await fetch(`${API_BASE}/metrics/compare?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  return (await response.json()) as ClinicsComparison;
}

/** Dashboard consolidado del dueño (todas las clínicas del scope en un request). */
export async function getOwnerDashboard(token: string, query: PeriodQuery): Promise<OwnerDashboard> {
  const params = new URLSearchParams();
  appendPeriodParams(params, query);
  const response = await fetch(`${API_BASE}/metrics/dashboard/owner?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  return (await response.json()) as OwnerDashboard;
}

export interface OwnerDashboardPair {
  current: OwnerDashboard;
  previous: OwnerDashboard;
}

/** Período actual + anterior (mes previo o mismo tramo del año previo). */
export async function getOwnerDashboardPair(
  token: string,
  query: PeriodQuery
): Promise<OwnerDashboardPair> {
  const params = new URLSearchParams();
  appendPeriodParams(params, query);
  params.set('withPrevious', '1');
  const response = await fetch(`${API_BASE}/metrics/dashboard/owner?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  return (await response.json()) as OwnerDashboardPair;
}

/** Dashboard consolidado owner para retención/PVP histórico de profesionales. */
export async function getOwnerPvpRetentionDashboard(
  token: string,
  query: PeriodQuery & { months?: number }
): Promise<OwnerPvpRetentionDashboard> {
  const params = new URLSearchParams();
  appendPeriodParams(params, query);
  if (query.months !== undefined) {
    params.set('months', String(query.months));
  }
  const response = await fetch(`${API_BASE}/metrics/dashboard/owner/pvp-retention?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  return (await response.json()) as OwnerPvpRetentionDashboard;
}

export type InsightSeverity = 'critical' | 'warning' | 'info';
export type InsightType = 'pvp_drop' | 'revenue_drop' | 'absenteeism_high';

export interface OwnerInsight {
  id: string;
  type: InsightType;
  severity: InsightSeverity;
  tenantId: string;
  titleKey: string;
  metrics: Record<string, number | string | null>;
  subjects: Array<{ id: string; name: string; metric: string }>;
  cta: 'clinic' | 'finances' | 'professionals' | 'patients' | 'overview';
}

export interface OwnerInsightsResponse {
  scope: 'owner-insights';
  period: string;
  previousPeriod: string;
  insights: OwnerInsight[];
  totals: {
    critical: number;
    warning: number;
    clinicsWithInsights: number;
  };
}

/** Insights multi-señal del business unit (umbral server-side). */
export async function getOwnerInsights(
  token: string,
  query: PeriodQuery & { months?: number; tenantId?: string }
): Promise<OwnerInsightsResponse> {
  const params = new URLSearchParams();
  appendPeriodParams(params, query);
  if (query.months !== undefined) {
    params.set('months', String(query.months));
  }
  if (query.tenantId) {
    params.set('tenantId', query.tenantId);
  }
  const response = await fetch(`${API_BASE}/metrics/dashboard/owner/insights?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }
  return (await response.json()) as OwnerInsightsResponse;
}

export type {
  OwnerProfessionalRankingRow,
  OwnerProfessionalsRanking,
  ProfesionalProductionTrend,
} from '@/application/metrics';

/** Ranking multi-clínica de profesionales (fan-out en API). */
export async function getOwnerProfessionalsRanking(
  token: string,
  query: PeriodQuery & { tenantId?: string; months?: number; limit?: number }
): Promise<OwnerProfessionalsRanking> {
  const params = new URLSearchParams({
    year: String(query.year),
    month: String(query.month ?? 1),
  });
  if (query.tenantId) params.set('tenantId', query.tenantId);
  if (query.months !== undefined) params.set('months', String(query.months));
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  const response = await fetch(`${API_BASE}/metrics/professionals/ranking?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }
  return (await response.json()) as OwnerProfessionalsRanking;
}

/** Nombre de archivo seguro (sin path ni caracteres reservados). */
export function sanitizeExportFilename(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[<>:"/\\|?*]+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 120);
}

function periodSlug(query: MetricsPeriodQuery): string {
  if (query.month != null) {
    return `${query.year}-${String(query.month).padStart(2, '0')}`;
  }
  return String(query.year);
}

/** Extrae el filename del header `Content-Disposition`, con fallback. */
function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) {
    return fallback;
  }
  const match = /filename="?([^";]+)"?/.exec(header);
  return match?.[1]?.trim() ?? fallback;
}

/** Timeout del pack multi-sección (batch + profesionales). */
export const DIRECTORY_PACK_TIMEOUT_MS = 45_000;

/** Descarga un binario protegido (Bearer) y dispara el guardado en el navegador. */
async function downloadReport(
  token: string,
  url: string,
  fallbackName: string,
  options?: { timeoutMs?: number }
): Promise<void> {
  const controller = options?.timeoutMs ? new AbortController() : undefined;
  const timer =
    controller && options?.timeoutMs
      ? setTimeout(() => controller.abort(), options.timeoutMs)
      : undefined;
  let response: Response;
  try {
    response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller?.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('export_timeout');
    }
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('export_timeout');
    }
    throw err;
  } finally {
    if (timer) clearTimeout(timer);
  }

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  const blob = await response.blob();
  const filename = filenameFromDisposition(response.headers.get('content-disposition'), fallbackName);
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

export interface DirectoryPackExportOptions {
  /** Base del fallback si el servidor no manda Content-Disposition (sin extensión). */
  filename?: string;
}

/** Exporta el pack “Directorio del mes” (PDF/XLSX). Scope = allowedTenantIds. */
export function downloadDirectoryPack(
  token: string,
  query: PeriodQuery,
  format: ReportFormat,
  options?: DirectoryPackExportOptions
): Promise<void> {
  const params = new URLSearchParams({ year: String(query.year), month: String(query.month), format });
  const fallback = sanitizeExportFilename(options?.filename?.trim() || `directorio-${periodSlug({
    tenantId: 'network',
    year: query.year,
    month: query.month,
  })}`);
  return downloadReport(
    token,
    `${API_BASE}/metrics/pack/export?${params.toString()}`,
    `${fallback}.${format}`,
    { timeoutMs: DIRECTORY_PACK_TIMEOUT_MS }
  );
}

/** Exporta la comparativa del scope (PDF/XLSX) como descarga. */
export function downloadComparisonReport(token: string, query: PeriodQuery, format: ReportFormat): Promise<void> {
  const params = new URLSearchParams({ year: String(query.year), month: String(query.month), format });
  return downloadReport(token, `${API_BASE}/metrics/compare/export?${params.toString()}`, `comparativa.${format}`);
}

export interface SummaryExportOptions {
  clinicName?: string;
  /** Base del fallback si el servidor no manda Content-Disposition (sin extensión). */
  filename?: string;
}

/** Exporta el resumen de una clínica/sede (PDF/XLSX) como descarga. */
export function downloadSummaryReport(
  token: string,
  query: MetricsPeriodQuery,
  format: ReportFormat,
  options?: SummaryExportOptions
): Promise<void> {
  const params = new URLSearchParams({ year: String(query.year), month: String(query.month), format });
  if (query.consultorioId !== undefined) {
    params.set('consultorioId', String(query.consultorioId));
  }
  const url = `${API_BASE}/metrics/tenants/${encodeURIComponent(query.tenantId)}/summary/export?${params.toString()}`;
  const clinic = sanitizeExportFilename(options?.clinicName?.trim() || query.tenantId);
  const fallbackBase = sanitizeExportFilename(
    options?.filename?.trim() || `resumen-${clinic}-${periodSlug(query)}`
  );
  return downloadReport(token, url, `${fallbackBase}.${format}`);
}

/** Exporta el reporte de caja de una clínica/sede (PDF/XLSX) como descarga. */
export function downloadCajaReport(token: string, query: MetricsPeriodQuery, format: ReportFormat): Promise<void> {
  const params = new URLSearchParams({ year: String(query.year), month: String(query.month), format });
  if (query.consultorioId !== undefined) {
    params.set('consultorioId', String(query.consultorioId));
  }
  const url = `${API_BASE}/metrics/tenants/${encodeURIComponent(query.tenantId)}/caja/export?${params.toString()}`;
  return downloadReport(token, url, `caja-${query.tenantId}.${format}`);
}

/** Exporta el reporte PVP (clínica + profesionales + alertas) PDF/XLSX. */
export function downloadPvpReport(token: string, query: MetricsPeriodQuery, format: ReportFormat): Promise<void> {
  const params = new URLSearchParams({ year: String(query.year), month: String(query.month), format });
  if (query.consultorioId !== undefined) {
    params.set('consultorioId', String(query.consultorioId));
  }
  const url = `${API_BASE}/metrics/tenants/${encodeURIComponent(query.tenantId)}/pvp/export?${params.toString()}`;
  return downloadReport(token, url, `pvp-${query.tenantId}.${format}`);
}

export type RecurrenceExportFormat = 'xlsx' | 'csv';

/** Exporta la lista de recurrencia CSV/XLSX. */
export function downloadRecurrenceExport(
  token: string,
  query: { tenantId: string; year: number; month: number; tipo?: string },
  format: RecurrenceExportFormat
): Promise<void> {
  const params = new URLSearchParams({
    year: String(query.year),
    month: String(query.month),
    format,
  });
  if (query.tipo) params.set('tipo', query.tipo);
  const url = `${API_BASE}/metrics/tenants/${encodeURIComponent(query.tenantId)}/recurrencia/detalle/export?${params.toString()}`;
  return downloadReport(token, url, `recurrencia-${query.tenantId}.${format}`);
}

/** Exporta la lista de recontacto (no-volvieron) CSV/XLSX. */
export function downloadNoVolvieronExport(
  token: string,
  query: NoVolvieronQuery & { planAgotado?: boolean },
  format: NoVolvieronExportFormat
): Promise<void> {
  const params = new URLSearchParams({
    year: String(query.year),
    month: String(query.month),
    format,
  });
  if (query.desde !== undefined) params.set('desde', String(query.desde));
  if (query.hasta !== undefined) params.set('hasta', String(query.hasta));
  if (query.planAgotado) params.set('planAgotado', '1');
  const url = `${API_BASE}/metrics/tenants/${encodeURIComponent(query.tenantId)}/pacientes/no-volvieron/export?${params.toString()}`;
  return downloadReport(token, url, `no-volvieron-${query.tenantId}.${format}`);
}

export type RetentionStatus =
  | 'normal'
  | 'observation'
  | 'alert'
  | 'critical'
  | 'insufficient_history';

/** Perfil PVP histórico de un profesional. */
export interface ProfesionalPvpProfile {
  tenantId: string;
  profesionalId: number;
  nombre: string;
  period: string;
  monthsAnalyzed: number;
  pvp: {
    actual: number;
    mesAnterior: number | null;
    variacionAbs: number | null;
    variacionPct: number | null;
    promedioHistorico: number | null;
    mejorReciente: number | null;
    peorReciente: number | null;
    mesesConsecutivosDescenso: number;
    trendPeriods: string[];
    trendValues: Array<number | null>;
    status: RetentionStatus;
    reasons: string[];
  };
  clinic: {
    pvp: number;
    pvpMesAnterior: number | null;
    pvpVariacionAbs: number | null;
  };
  network: {
    pvpAverage: number | null;
  };
}

export async function getProfesionalPvpProfile(
  token: string,
  query: MetricsPeriodQuery & { profesionalId: number; months?: number }
): Promise<ProfesionalPvpProfile> {
  const params = new URLSearchParams({
    year: String(query.year),
    month: String(query.month ?? 1),
  });
  if (query.months !== undefined) {
    params.set('months', String(query.months));
  }
  const response = await fetch(
    `${API_BASE}/metrics/tenants/${encodeURIComponent(query.tenantId)}/profesionales/${query.profesionalId}/pvp-profile?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }
  return (await response.json()) as ProfesionalPvpProfile;
}

export type EmailReportKind = 'summary' | 'caja' | 'comparison' | 'generic' | 'pvp';

export interface EmailReportTable {
  title: string;
  columns: Array<{ key: string; header: string; align?: 'left' | 'right' }>;
  rows: Array<Record<string, string | number>>;
}

export interface EmailReportPayload {
  to: string;
  subject?: string;
  message?: string;
  kind: EmailReportKind;
  year: number;
  month: number;
  tenantId?: string;
  consultorioId?: number;
  title?: string;
  description?: string;
  kpis?: Array<{ label: string; value: string | number }>;
  /** Tablas de texto para kind=generic (render server-side con pdfkit). */
  tables?: EmailReportTable[];
}

/** Genera el PDF del reporte (mismos filtros) y lo envía por email vía SendGrid. */
export async function emailReport(
  token: string,
  payload: EmailReportPayload
): Promise<{ ok: true; to: string; filename: string }> {
  const response = await fetch(`${API_BASE}/metrics/reports/email`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }
  return (await response.json()) as { ok: true; to: string; filename: string };
}
